import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Calendar, 
  MapPin, 
  Users, 
  Mic2, 
  LogOut,
  Menu,
  X,
  Layers,
  Settings,
  Image,
  Bot,
  Mail,
  ShieldCheck,
  Download,
  Database,
  Trophy
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Toaster } from '../ui/sonner';

const LOGO_URL = "/assets/sios-logo.png";

const sidebarLinks = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/editions', label: 'Edizioni', icon: Calendar },
  { href: '/admin/events', label: 'Eventi', icon: MapPin },
  { href: '/admin/import', label: 'Import Evento', icon: Download },
  { href: '/admin/challenges', label: 'Startup Challenge', icon: Trophy },
  { href: '/admin/speakers', label: 'Speakers', icon: Users },
  { href: '/admin/sessions', label: 'Sessions', icon: Mic2 },
  { href: '/admin/tracks', label: 'Tracks', icon: Layers },
  { href: '/admin/galleries', label: 'Gallery', icon: Image },
  { href: '/admin/chatbot', label: 'Chatbot Partner', icon: Bot },
  { href: '/admin/newsletter', label: 'Newsletter', icon: Mail },
  { href: '/admin/settings', label: 'Settings', icon: Settings },
  { href: '/admin/export', label: 'Export DB', icon: Database },
  { href: '/admin/admins', label: 'Gestione Admin', icon: ShieldCheck, superAdminOnly: true },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-sios-void flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-sios-surface border-r border-white/5 fixed h-full" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SIOS" className="h-8 w-auto" />
            <span className="font-heading font-semibold text-sm text-muted-foreground">Admin</span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {sidebarLinks
            .filter(link => !link.superAdminOnly || user?.is_super_admin)
            .map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.href)
                    ? 'bg-primary/20 text-primary border border-primary/30'
                    : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                }`}
                data-testid={`sidebar-link-${link.label.toLowerCase()}`}
              >
                <Icon size={18} />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-sios-surface border-b border-white/5 flex items-center justify-between px-4 z-50">
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="SIOS" className="h-6 w-auto" />
          <span className="font-heading font-semibold text-xs text-muted-foreground">Admin</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-foreground">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <aside 
            className="w-64 h-full bg-sios-surface border-r border-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/5 mt-16">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <nav className="p-4 space-y-1">
              {sidebarLinks
                .filter(link => !link.superAdminOnly || user?.is_super_admin)
                .map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive(link.href)
                        ? 'bg-primary/20 text-primary'
                        : 'text-muted-foreground hover:bg-white/5'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={18} />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-white/5">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-destructive"
                onClick={handleLogout}
              >
                <LogOut size={18} className="mr-3" />
                Logout
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
      <Toaster position="bottom-right" />
    </div>
  );
};
